import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchCategoriesGqlQuery,
  FetchCategoriesCacheKey,
  FetchFinCategoriesFilters,
  FetchCategoriesSort,
  FetchCategoriesPage,
  FetchCategoriesLimit
} from '../../categoriesTypes';
import { InfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useInfiniteIndexQuery';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  CategoriesPermissions,
  INITIAL_CATEGORIES_FILTERS,
  INITIAL_CATEGORIES_LIMIT,
  INITIAL_CATEGORIES_SORT
} from '../../categoriesConstants';

interface CategoriesDefaultOptions {
  cacheKey: FetchCategoriesCacheKey;
  cacheQuery: FetchCategoriesGqlQuery;
  initialFilters?: FetchFinCategoriesFilters;
  initialLimit?: FetchCategoriesLimit;
  initialPage?: FetchCategoriesPage;
  initialSort?: FetchCategoriesSort;
  query: FetchCategoriesGqlQuery;
  options?: {
    cacheTime?: number;
    staleTime?: number;
  };
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  action?: CategoriesPermissions;
}

interface CategoriesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface CategoriesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

type CategoriesOptions = CategoriesDefaultOptions &
  (CategoriesWithPrefetchItemOptions | CategoriesWithoutPrefetchItemOptions);

const scope = 'categories';

function useFinInfiniteCategories<
  ItemType extends InfiniteIndexQueryBaseNodeType
>({
  cacheKey,
  cacheQuery,
  initialFilters = INITIAL_CATEGORIES_FILTERS,
  initialLimit = INITIAL_CATEGORIES_LIMIT,
  initialSort = INITIAL_CATEGORIES_SORT,
  options = {},
  query,
  source = FetchCacheItemsSources.ES,
  trackTotalHits = false,
  action = CategoriesPermissions.READ_CATEGORIES_INDEX_ES_QUERY
}: CategoriesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    clearItemsFilters,
    filterItems,
    sortItems,
    limitItems,
    isFetchingNextPage,
    hasNextPage,
    loadMoreItems,
    changeItemsFilters
  } = useCacheInfiniteIndexQuery<ItemType>({
    action,
    cacheKey,
    cacheQuery: cacheQuery ? cacheQuery : query,
    defaultApi: 'cache_api',
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits
  });

  return {
    categoriesData: data,
    categories: items,
    categoriesError: itemsError,
    categoriesTotalCount: itemsTotalCount,
    categoriesFetched: isFetched,
    categoriesLoading: isLoading,
    categoriesIsPlaceholderData: isPlaceholderData,
    categoriesFilters: currentFilters,
    categoriesSort: currentSort,
    categoriesPage: currentPage,
    categoriesLimit: currentLimit,
    filterCategories: filterItems,
    clearCategoriesFilters: clearItemsFilters,
    sortCategories: sortItems,
    limitCategories: limitItems,
    hasNextCategoriesPage: hasNextPage,
    isFetchingNextCategoriesPage: isFetchingNextPage,
    loadMoreCategories: loadMoreItems,
    changeCategoriesFilters: changeItemsFilters
  };
}

export default useFinInfiniteCategories;
